import { Injectable, OnDestroy } from '@angular/core';
import { ChildActivationEnd, NavigationStart, NavigationEnd, ParamMap, Router, RouterEvent, Scroll, Event } from '@angular/router';

import { filter, map, mergeMap, pairwise, shareReplay, startWith } from 'rxjs/operators';
import { NEVER, Observable, of, Subject, merge, Subscription } from 'rxjs';

import { ReportsRouteModel } from '../../ReportsRouter/models/reports-route.model';
import { ReportsRouteTypeEnum, reportsUrlParse } from '../../../Common/Reports/enums/reports-route-type.enum';
import { MonitorReportsNavigationService } from '../../../Common/MonitorShared/services/monitor-reports-navigation.service';


@Injectable()
export class ReportsRoutingService implements OnDestroy {
    private subscriptions: Subscription[] = [];
    private navOutsideReports$ = new Subject<null>();
    private queryParamMap: ParamMap;
    private isReload: boolean = true;

    private currentRoute$: Observable<ReportsRouteModel> = this.router.events.pipe(
        filter((e: Event | RouterEvent) => e instanceof RouterEvent || e instanceof ChildActivationEnd || e instanceof Scroll),
        map<RouterEvent, ReportsRouteModel>(routerEvent => {
            // child activation end occurs before navigation end
            if (routerEvent instanceof ChildActivationEnd) {
                this.queryParamMap = routerEvent.snapshot.queryParamMap;
            }

            if (routerEvent instanceof Scroll && this.isReload) {
                this.isReload = false;
                const route = this.getRouteModel((routerEvent.routerEvent as NavigationEnd).urlAfterRedirects);
                if (!route) {
                    this.navOutsideReports$.next(null);
                }

                return route;
            }

            if (routerEvent instanceof NavigationEnd) {
                this.isReload = false;
                const route = this.getRouteModel((routerEvent as NavigationEnd).urlAfterRedirects);
                if (!route) {
                    this.navOutsideReports$.next(null);
                }

                return route;
            }
        }),
        // If the routeModel couldn't be generated, don't emit
        filter(e => !!e),
        // Seed with the already-active route in case this service was built after NavigationEnd/Scroll
        // already fired for the current navigation (eg. Reports' async canActivate guard delays construction)
        startWith(this.getInitialRoute()),
        filter(e => !!e),
        shareReplay(1)
    );

    constructor(
        private router: Router,
        private monitorReportsNavService: MonitorReportsNavigationService
    ) {
        this.subscriptions.push(
            this.currentRoute$.subscribe()
        );
    }

    ngOnDestroy(): void {
        this.subscriptions?.forEach(sub => sub.unsubscribe());
    }

    public getCurrentRoute$(): Observable<ReportsRouteModel> {
        return this.currentRoute$;
    }

    /**
     * Given an observable that tracks the current mode, returns an observable that tracks the
     * current mode and whether it has changed. Useful because subscriptions to this service's currentRoute
     * fire even when we are outside the feature. This allows us to determine whether we are leaving or entering
     * the feature (and subfeature/mode).
     * @param getCurrentMode$ An observable that emits the current mode on route event
     * @returns An observable that emits on route event, detailing whether the mode has changed from previous value
     */
    public getModeChange(
        getCurrentMode$: Observable<number>
    ): Observable<{ isModeChange: boolean; currentMode: number}> {
        return merge(
            this.navOutsideReports$,
            getCurrentMode$
        ).pipe(
            startWith(null),
            pairwise(),
            mergeMap(([prev, curr]) => {
                if (curr === null) {
                    // We are currently outside monitoring
                    return NEVER;
                } else {
                    // False means navigation with a subfeature
                    // True means navigation to a new subfeature
                    return of({
                        isModeChange: prev !== curr,
                        currentMode: curr
                    });
                }
            }),
        );
    }

    public goToSharedReports(): void {
        this.monitorReportsNavService.goToSharedReports();
    }

    public gotoReports(options?: any): void {
        this.monitorReportsNavService.gotoReports(options);
    }

    public gotoReport(id?: string | number, quickReport: boolean = false, cloneId?: number): void {
        this.monitorReportsNavService.gotoReport(id, quickReport, cloneId);
    }



    /**
     * Returns a route model from the router's current state, without waiting for a future router event.
     */
    private getInitialRoute(): ReportsRouteModel {
        this.queryParamMap = this.router.routerState.snapshot.root.queryParamMap;
        return this.getRouteModel(this.router.url);
    }

    /**
     * Returns a route model based on a url passed in
     *
     * @param url -  url to base route model on
     *
     * @returns ReportsRouteModel
     */
    private getRouteModel(url: string): ReportsRouteModel {
        const matches = url.match(reportsUrlParse);
        let route: ReportsRouteModel;
        if (!matches) {
            return null;
        } else {
            switch (matches[2]) {
                // Social/themes
                case 'report':
                    if (matches[3]) {
                        route = new ReportsRouteModel(ReportsRouteTypeEnum.report, { reportId: matches[3] });
                    } else {
                        route = new ReportsRouteModel(ReportsRouteTypeEnum.report, undefined, this.queryParamMap);
                    }
                    break;
                case 'shared':
                    route = new ReportsRouteModel(ReportsRouteTypeEnum.reportSharedReports);
                    break;
                default:
                    // Social/themes/theme fallback
                    route = new ReportsRouteModel(ReportsRouteTypeEnum.reportsHype);
                    break;
            }
        }
        return route;
    }
}
